import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    List,
    ListItemButton,
    Button,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    CircularProgress,
    Paper,
    IconButton,
    Tabs,
    Tab,
} from '@mui/material';
import {
    ArrowForward as BackIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import moment from 'moment-jalaali';
import useFetch from '../../../../../hooks/useFetch';
import { API } from '../../../../../utils/apiRoutes';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import PersianMonthPicker from '../../../../common/PersianMonthPicker';

const PERSIAN_MONTHS = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
];

// First day of the Jalali month containing `date`, as a moment (or null). The month
// picker returns a Gregorian Date for a Jalali month selection; snap via jYear/jMonth —
// setting day=1 on the Gregorian date can shift the Jalali month backward.
const jalaliMonthStart = (date) => {
    if (!date || isNaN(new Date(date).getTime())) return null;
    const m = moment(date);
    const first = moment(
        `${m.jYear()}/${String(m.jMonth() + 1).padStart(2, '0')}/01`,
        'jYYYY/jMM/jDD',
        true,
    );
    return first.isValid() ? first : null;
};

const PAYMENT_METHOD_LABELS = { cash: 'نقدی', transfer: 'انتقال به حساب', check: 'چک' };

const formatCurrency = (amount) => toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));

const monthLabel = (ym) => {
    if (!ym) return '';
    const [y, m] = String(ym).split('-').map(Number);
    return `${PERSIAN_MONTHS[(m || 1) - 1] || ''} ${toPersianDigits(y || '')}`.trim();
};

const formatDate = (d) => {
    if (!d) return '-';
    try {
        return toPersianDigits(new Date(d).toLocaleDateString('fa-IR'));
    } catch {
        return d;
    }
};

const remainingColor = (v) => ((v || 0) > 0 ? 'warning.main' : 'success.main');

// Credit-normal balance: >0 means the institute owes the teacher (بستانکار).
const balanceLabel = (v) => {
    const n = v || 0;
    if (n === 0) return formatCurrency(0);
    return `${formatCurrency(Math.abs(n))} ${n > 0 ? 'بستانکار' : 'بدهکار'}`;
};

function TeacherLedger({ teacher, personType = 'teacher', onBack, onAddPayment, onEditPayment, onDeletePayment, onApprovePayment, refreshToken }) {
    const isStaff = personType === 'staff';
    const idKey = isStaff ? 'staff_id' : 'teacher_id';
    // Whole Jalali months only (first-day-of-month Dates). The ledger buckets payable by
    // Jalali month and attributes settlements by settlement_period, so a mid-month day
    // boundary would truncate payable while paid still counts in full — producing fake
    // negative balances in boundary months.
    const [monthRange, setMonthRange] = useState({ from: null, to: null });
    const [selected, setSelected] = useState(null);
    const [view, setView] = useState(0); // 0: ماهانه, 1: صورت‌حساب

    // Reset selection/range whenever a different person's ledger opens.
    useEffect(() => {
        setSelected(null);
        setMonthRange({ from: null, to: null });
    }, [teacher?.teacher_id]);

    // Keep the range valid: `from` must never come after `to`. Both store the first day
    // of their Jalali month, so a plain getTime() comparison orders them by month.
    const handleFromChange = (date) => {
        const start = jalaliMonthStart(date);
        const d = start ? start.toDate() : null;
        setMonthRange((prev) => ({
            from: d,
            to: (d && prev.to && d.getTime() > prev.to.getTime()) ? d : prev.to,
        }));
    };
    const handleToChange = (date) => {
        const start = jalaliMonthStart(date);
        const d = start ? start.toDate() : null;
        setMonthRange((prev) => ({
            from: (d && prev.from && d.getTime() < prev.from.getTime()) ? d : prev.from,
            to: d,
        }));
    };

    // Months rail for this person over the chosen range (defaults to the current
    // Jalali year). status=all so the person is always found. The API takes Gregorian
    // dates; snap them to the full Jalali months so boundary months are never partial.
    const monthsParams = useMemo(() => {
        const p = { status: 'all', person: personType };
        if (teacher?.teacher_id) p[idKey] = teacher.teacher_id;
        if (monthRange.from) p.start_date = moment(monthRange.from).format('YYYY-MM-DD');
        if (monthRange.to) {
            p.end_date = moment(monthRange.to).add(1, 'jMonth').subtract(1, 'day').format('YYYY-MM-DD');
        }
        return p;
    }, [teacher?.teacher_id, monthRange, personType, idKey]);

    const { data: monthsData, loading: monthsLoading, refetch: refetchMonths } = useFetch(API.teacherSettlementSummaryMonthly, {
        params: monthsParams,
        skip: !teacher?.teacher_id,
    });

    const months = monthsData?.[0]?.months || [];
    const openingBalance = monthsData?.[0]?.opening_balance || 0;
    // Months arrive oldest-first; default the rail to the most recent month.
    const selectedMonth = (selected && months.some((m) => m.year_month === selected))
        ? selected
        : (months[months.length - 1]?.year_month || null);

    const { data, loading, refetch } = useFetch(API.teacherSettlementTransactions, {
        params: (teacher?.teacher_id && selectedMonth)
            ? { person: personType, [idKey]: teacher.teacher_id, year_month: selectedMonth }
            : undefined,
        skip: !teacher?.teacher_id || !selectedMonth,
    });

    // Refetch the rail and this month's detail after the parent mutates a payment.
    useEffect(() => {
        if (refreshToken && teacher?.teacher_id) {
            refetchMonths();
            if (selectedMonth) refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshToken]);

    const sessions = data?.sessions || [];
    const payments = data?.payments || [];

    // Build a settlement-shaped object the shared form/dialogs understand.
    const toSettlement = (p) => ({
        ...p,
        [isStaff ? 'staff' : 'teacher']: teacher?.teacher_id,
        teacher_name: teacher?.teacher_name, // for the delete/approve dialogs
        staff_name: isStaff ? teacher?.teacher_name : undefined,
        settlement_period: p.settlement_period || data?.year_month,
    });

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Button startIcon={<BackIcon />} onClick={onBack} variant="outlined">
                    بازگشت به خلاصه
                </Button>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    دفتر {teacher?.teacher_name}
                </Typography>
            </Box>

            {/* Tabs + month-range filter share one bar so the filter sits next to what it filters */}
            <Paper sx={{ mb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Tabs value={view} onChange={(e, v) => setView(v)}>
                        <Tab label="ماهانه" />
                        <Tab label="صورت‌حساب" />
                    </Tabs>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ml: 'auto', px: 2, py: 1 }}>
                        <Box sx={{ width: 170 }}>
                            <PersianMonthPicker
                                label="از ماه"
                                value={monthRange.from}
                                onChange={handleFromChange}
                            />
                        </Box>
                        <Box sx={{ width: 170 }}>
                            <PersianMonthPicker
                                label="تا ماه"
                                value={monthRange.to}
                                onChange={handleToChange}
                            />
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* صورت‌حساب: running debit/credit statement */}
            {view === 1 && (
                <Paper variant="outlined">
                    {monthsLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {!monthsLoading && (() => {
                        let running = openingBalance;
                        return (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ماه</TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: 'pre-line', lineHeight: 1.3 }}>{"بدهکار\n(پرداخت)"}</TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: 'pre-line', lineHeight: 1.3 }}>{"بستانکار\n(درآمد)"}</TableCell>
                                        <TableCell align="center">باقیمانده</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'medium' }}>مانده ابتدای دوره</TableCell>
                                        <TableCell align="center">—</TableCell>
                                        <TableCell align="center">—</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>{balanceLabel(openingBalance)}</TableCell>
                                    </TableRow>
                                    {months.map((m) => {
                                        running += (m.total_payable || 0) - (m.paid_amount || 0);
                                        return (
                                            <TableRow key={m.year_month}>
                                                <TableCell>{monthLabel(m.year_month)}</TableCell>
                                                <TableCell align="center" sx={{ color: 'success.main' }}>{formatCurrency(m.paid_amount)}</TableCell>
                                                <TableCell align="center" sx={{ color: 'primary.main' }}>{formatCurrency(m.total_payable)}</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', color: remainingColor(running) }}>{balanceLabel(running)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {months.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Typography align="center" variant="body2" color="text.secondary">تراکنشی در این بازه یافت نشد.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>مانده پایان دوره</TableCell>
                                        <TableCell align="center">—</TableCell>
                                        <TableCell align="center">—</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', color: remainingColor(running) }}>{balanceLabel(running)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        );
                    })()}
                </Paper>
            )}

            {view === 0 && (
            <Grid container spacing={2}>
                {/* Months rail */}
                <Grid item xs={12} sm={4} md={3}>
                    <Paper variant="outlined">
                        {monthsLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                        <List disablePadding>
                            {!monthsLoading && months.length === 0 && (
                                <Box sx={{ p: 2 }}>
                                    <Typography align="center" variant="body2" color="text.secondary">ماهی در این بازه یافت نشد.</Typography>
                                </Box>
                            )}
                            {months.map((m) => (
                                <ListItemButton
                                    key={m.year_month}
                                    selected={m.year_month === selectedMonth}
                                    onClick={() => setSelected(m.year_month)}
                                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        {monthLabel(m.year_month)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: remainingColor(m.remaining_balance) }}>
                                        {formatCurrency(m.remaining_balance)}
                                    </Typography>
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                {/* Detail pane */}
                <Grid item xs={12} sm={8} md={9}>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && data && (
                        <Box>
                            {/* Reconciliation line */}
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {monthLabel(data.year_month)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    قابل پرداخت:{' '}
                                    <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        {formatCurrency(data.total_payable)}
                                    </Box>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    پرداخت شده:{' '}
                                    <Box component="span" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                        {formatCurrency(data.paid_amount)}
                                    </Box>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    مانده:{' '}
                                    <Box component="span" sx={{ fontWeight: 'bold', color: remainingColor(data.remaining_balance) }}>
                                        {formatCurrency(data.remaining_balance)}
                                    </Box>
                                </Typography>
                            </Box>

                            {/* Income (sessions) */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                درآمد ({toPersianDigits(sessions.length)} جلسه)
                            </Typography>
                            <Paper variant="outlined" sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>تاریخ</TableCell>
                                            <TableCell>دانش‌آموز</TableCell>
                                            <TableCell>درس</TableCell>
                                            <TableCell>وضعیت</TableCell>
                                            <TableCell align="center">مبلغ</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sessions.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    <Typography align="center" variant="body2" color="text.secondary">جلسه‌ای ثبت نشده است.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {sessions.map((s) => (
                                            <TableRow key={s.operation_id}>
                                                <TableCell>{formatDate(s.date)}</TableCell>
                                                <TableCell>{s.student_name}</TableCell>
                                                <TableCell>{s.course_name}</TableCell>
                                                <TableCell>{s.status}</TableCell>
                                                <TableCell align="center">{formatCurrency(s.amount)}</TableCell>
                                            </TableRow>
                                        ))}
                                        {data.fixed_salary_payable > 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4}>حقوق ثابت</TableCell>
                                                <TableCell align="center">{formatCurrency(data.fixed_salary_payable)}</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Paper>

                            {/* Payments */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    پرداخت‌ها ({toPersianDigits(payments.length)})
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    sx={{ mr: 'auto' }}
                                    onClick={() => onAddPayment && onAddPayment(data.year_month)}
                                >
                                    افزودن پرداخت
                                </Button>
                            </Box>
                            <Paper variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>تاریخ پرداخت</TableCell>
                                            <TableCell align="center">مبلغ</TableCell>
                                            <TableCell>روش</TableCell>
                                            <TableCell>شماره مرجع</TableCell>
                                            <TableCell>وضعیت</TableCell>
                                            <TableCell align="center">عملیات</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {payments.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6}>
                                                    <Typography align="center" variant="body2" color="text.secondary">پرداختی برای این ماه ثبت نشده است.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {payments.map((p) => (
                                            <TableRow key={p.id}>
                                                <TableCell>{formatDate(p.payment_date)}</TableCell>
                                                <TableCell align="center">{formatCurrency(p.amount)}</TableCell>
                                                <TableCell>{PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method}</TableCell>
                                                <TableCell>{p.reference_number || '-'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={p.is_approved ? 'تأیید شده' : 'در انتظار تأیید'}
                                                        color={p.is_approved ? 'success' : 'warning'}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                        {!p.is_approved && (
                                                            <IconButton size="small" color="success" aria-label="تأیید"
                                                                onClick={() => onApprovePayment && onApprovePayment(toSettlement(p))}>
                                                                <CheckCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                        {p.receipt && (
                                                            <IconButton size="small" aria-label="مشاهده رسید"
                                                                onClick={() => window.open(p.receipt, '_blank')}>
                                                                <AttachFileIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                        <IconButton size="small" color="primary" aria-label="ویرایش"
                                                            onClick={() => onEditPayment && onEditPayment(toSettlement(p))}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" color="error" aria-label="حذف"
                                                            onClick={() => onDeletePayment && onDeletePayment(toSettlement(p))}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    )}
                </Grid>
            </Grid>
            )}
        </Box>
    );
}

export default TeacherLedger;
